import React, { InputHTMLAttributes, TextareaHTMLAttributes, forwardRef } from 'react';

// ============================================
// Types
// ============================================

interface BaseInputProps {
  label?: string;
  error?: string;
  hint?: string;
}

interface InputProps extends InputHTMLAttributes<HTMLInputElement>, BaseInputProps {}

interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement>, BaseInputProps {
  rows?: number;
}

interface SelectProps extends React.SelectHTMLAttributes<HTMLSelectElement>, BaseInputProps {
  options: Array<{ value: string; label: string }>;
}

// ============================================
// Base Styles
// ============================================

const labelStyles = `
  block text-[11px] font-black uppercase tracking-widest 
  text-slate-400 mb-2 ml-1
`;

const baseInputStyles = `
  w-full px-5 py-4 
  bg-slate-50/50 rounded-2xl 
  border border-slate-200 
  focus:bg-white focus:border-indigo-500 
  focus:ring-4 focus:ring-indigo-500/10 
  outline-none transition-all 
  placeholder-slate-400 
  text-slate-900 font-semibold
`;

const errorInputStyles = `
  border-rose-300 focus:border-rose-500 
  focus:ring-rose-500/10
`;

const errorTextStyles = `
  text-[10px] font-bold text-rose-500 mt-1.5 ml-1
`;

const hintTextStyles = `
  text-[10px] font-medium text-slate-400 mt-1.5 ml-1
`;

// ============================================
// Input Component
// ============================================

export const Input = forwardRef<HTMLInputElement, InputProps>(({
  label,
  error,
  hint,
  className = '',
  ...props
}, ref) => {
  return (
    <div className="w-full">
      {label && (
        <label className={labelStyles}>
          {label}
          {props.required && <span className="text-rose-400 ml-0.5">*</span>}
        </label>
      )}
      <input
        ref={ref}
        className={`
          ${baseInputStyles}
          ${error ? errorInputStyles : ''}
          ${className}
        `}
        {...props}
      />
      {error && <p className={errorTextStyles}>{error}</p>}
      {hint && !error && <p className={hintTextStyles}>{hint}</p>}
    </div>
  );
});

Input.displayName = 'Input';

// ============================================
// Textarea Component
// ============================================

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(({
  label,
  error,
  hint,
  rows = 4,
  className = '',
  ...props
}, ref) => {
  return (
    <div className="w-full">
      {label && (
        <label className={labelStyles}>
          {label}
          {props.required && <span className="text-rose-400 ml-0.5">*</span>}
        </label>
      )}
      <textarea
        ref={ref}
        rows={rows}
        className={`
          ${baseInputStyles}
          resize-none
          ${error ? errorInputStyles : ''}
          ${className}
        `}
        {...props}
      />
      {error && <p className={errorTextStyles}>{error}</p>}
      {hint && !error && <p className={hintTextStyles}>{hint}</p>}
    </div>
  );
});

Textarea.displayName = 'Textarea';

// ============================================
// Select Component
// ============================================

export const Select = forwardRef<HTMLSelectElement, SelectProps>(({
  label,
  error,
  hint,
  options,
  className = '',
  ...props
}, ref) => {
  return (
    <div className="w-full">
      {label && (
        <label className={labelStyles}>
          {label}
          {props.required && <span className="text-rose-400 ml-0.5">*</span>}
        </label>
      )}
      <select
        ref={ref}
        className={`
          ${baseInputStyles}
          cursor-pointer
          ${error ? errorInputStyles : ''}
          ${className}
        `}
        {...props}
      >
        {options.map(option => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
      {error && <p className={errorTextStyles}>{error}</p>}
      {hint && !error && <p className={hintTextStyles}>{hint}</p>}
    </div>
  );
});

Select.displayName = 'Select';

// ============================================
// Search Input Component
// ============================================

interface SearchInputProps extends Omit<InputProps, 'type'> {
  onSearch?: (value: string) => void;
}

export const SearchInput = forwardRef<HTMLInputElement, SearchInputProps>(({
  onSearch,
  className = '',
  ...props
}, ref) => {
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    props.onChange?.(e);
    onSearch?.(e.target.value);
  };

  return (
    <div className="relative">
      <input
        ref={ref}
        type="text"
        className={`
          w-full pl-10 pr-4 py-2.5
          bg-white border border-slate-200
          focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/10
          rounded-xl text-sm outline-none transition-all
          placeholder-slate-400 font-semibold text-slate-900
          shadow-sm
          ${className}
        `}
        onChange={handleChange}
        {...props}
      />
      <div className="absolute left-3.5 top-3 text-slate-400">
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
        </svg>
      </div>
    </div>
  );
});

SearchInput.displayName = 'SearchInput';

export default Input;
